import type { AirDatepickerOptions, AirDatepickerPosition } from 'air-datepicker';

type PositionCallback = Extract<AirDatepickerOptions['position'], (...args: never[]) => unknown>;

/**
 * Zoom-safe replacement for air-datepicker's string `position` option.
 *
 * The library's built-in positioning reads the input's getBoundingClientRect()
 * + window.scrollY (viewport pixels) and writes the result as `top/left` px on
 * a popup that lives inside `html { zoom: 75% }` (index.css, <= 1536px). Those
 * px get scaled by the zoom a second time, so the calendar lands ~25% short of
 * the input - the further down/right the input, the bigger the gap.
 *
 * Here the input's rect is taken relative to the popup's containing block (both
 * rects share one coordinate space, so the difference is exact) and divided by
 * the zoom factor actually in effect. The factor is measured, not read from
 * CSS: <html>'s rect width over its layout offsetWidth is 0.75 on engines whose
 * rects are zoomed (Chrome 128+, Firefox, Safari) and 1 on engines whose rects
 * are not - which are exactly the engines where no correction is needed.
 *
 * `position` and `offset` mean the same as the library's own options
 * ('bottom left' / 12 are its defaults); the math mirrors its setPosition.
 */
export const zoomSafeDatepickerPosition = (
  position: AirDatepickerPosition | 'top center' | 'bottom center' = 'bottom left',
  offset = 12
): PositionCallback => {
  return ({ $datepicker, $target, done }) => {
    const html = document.documentElement;
    const zoom = html.offsetWidth ? html.getBoundingClientRect().width / html.offsetWidth : 1;

    // Absolute `top/left` are relative to the padding box of the nearest
    // positioned ancestor. offsetParent reports <body> when there is none, but
    // a static <body> is not the containing block - the initial containing
    // block (<html>'s origin) is.
    const offsetParent = $datepicker.offsetParent as HTMLElement | null;
    const base =
      offsetParent &&
      !(offsetParent === document.body && getComputedStyle(offsetParent).position === 'static')
        ? offsetParent
        : html;
    const baseRect = base.getBoundingClientRect();
    const targetRect = $target.getBoundingClientRect();
    const self = $datepicker.getBoundingClientRect();

    const target = {
      top: (targetRect.top - baseRect.top) / zoom - base.clientTop,
      left: (targetRect.left - baseRect.left) / zoom - base.clientLeft,
      width: targetRect.width / zoom,
      height: targetRect.height / zoom,
    };
    const width = self.width / zoom;
    const height = self.height / zoom;

    const [main = '', secondary = ''] = position.split(' ');
    let top = 0;
    let left = 0;

    switch (main) {
      case 'top':
        top = target.top - height - offset;
        break;
      case 'right':
        left = target.left + target.width + offset;
        break;
      case 'bottom':
        top = target.top + target.height + offset;
        break;
      case 'left':
        left = target.left - width - offset;
        break;
    }

    switch (secondary) {
      case 'top':
        top = target.top;
        break;
      case 'right':
        left = target.left + target.width - width;
        break;
      case 'bottom':
        top = target.top + target.height - height;
        break;
      case 'left':
        left = target.left;
        break;
      case 'center':
        if (/left|right/.test(main)) {
          top = target.top + target.height / 2 - height / 2;
        } else {
          left = target.left + target.width / 2 - width / 2;
        }
        break;
    }

    $datepicker.style.left = `${left}px`;
    $datepicker.style.top = `${top}px`;

    // With a custom position the library leaves DOM teardown to us; `done`
    // detaches the popup. Called straight away (as the library's README does)
    // rather than after transitionend, which would detach a popup that was
    // re-opened before its fade-out finished.
    return () => done();
  };
};
